import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { KeyRound, ShieldCheck } from "lucide-react";

export default function ChangePasswordCard() {
  const [status, setStatus] = useState(null); // { customized: bool }
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/auth/password-status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (form.next.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("La confirmación no coincide");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Contraseña actualizada");
      setForm({ current: "", next: "", confirm: "" });
      setStatus({ customized: true });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="change-password-card"
      className="rounded-md border border-zinc-800 bg-zinc-950 p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4 text-zinc-400" />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          Seguridad
        </div>
      </div>
      <h3 className="text-lg font-semibold text-white">Cambiar contraseña</h3>
      <p className="mt-1 text-xs text-zinc-500 max-w-xl">
        La nueva contraseña se guarda como hash bcrypt en un fichero local y
        tiene prioridad sobre la del archivo <code className="font-mono">.env</code>.
      </p>
      <div className="mt-3 inline-flex items-center gap-2 text-[11px]">
        <ShieldCheck
          className={`h-3.5 w-3.5 ${status?.customized ? "text-emerald-400" : "text-amber-400"}`}
        />
        <span className={status?.customized ? "text-emerald-400" : "text-amber-400"}>
          {status?.customized
            ? "Contraseña personalizada activa"
            : "Usando contraseña del archivo .env"}
        </span>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-4 max-w-md">
        <div className="space-y-2">
          <Label className="text-zinc-300">Contraseña actual</Label>
          <Input
            data-testid="change-password-current"
            type="password"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            autoComplete="current-password"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Nueva contraseña</Label>
          <Input
            data-testid="change-password-new"
            type="password"
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            autoComplete="new-password"
            minLength={6}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Confirmar nueva contraseña</Label>
          <Input
            data-testid="change-password-confirm"
            type="password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            autoComplete="new-password"
            minLength={6}
          />
        </div>
        <div className="flex justify-end pt-1">
          <Button
            type="submit"
            data-testid="change-password-submit"
            disabled={saving || !form.current || !form.next || !form.confirm}
            className="bg-white text-black hover:bg-zinc-200"
          >
            {saving ? "Actualizando…" : "Actualizar contraseña"}
          </Button>
        </div>
      </form>
    </div>
  );
}
